/**
 * Predictive Search Component
 * Uses Shopify's native predictive search API
 */

// Debounce utility
function searchDebounce(fn, wait = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

if (!customElements.get("predictive-search")) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.input = this.querySelector(".js-search-input");
      this.productTypeSelect = this.querySelector(".js-search-product-types");
      this.productTypeInput = this.querySelector("#product_type_input");
      this.resetBtn = this.querySelector(".js-search-reset");
      this.results = this.querySelector(".js-search-results");
      this.overlay = this.querySelector(".js-search-overlay");
      this.statusEl = this.querySelector(".js-search-status");
      this.loadingText = this.getAttribute("data-loading-text") || "Loading...";
      
      if (this.input) {
        this.addListeners();
      }
    }

    /**
     * Triggers when the web component is removed from the DOM
     */
    disconnectedCallback() {
      this.close();
    }

    addListeners() {
      this.input.addEventListener("focus", this.handleFocus.bind(this));
      this.input.addEventListener(
        "input",
        searchDebounce(this.handleInput.bind(this), 300)
      );
      
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener(
          "change",
          this.handleProductTypeChange.bind(this)
        );
      }
      
      const form = this.querySelector(".search");
      if (form) {
        form.addEventListener("submit", this.handleSubmit.bind(this));
      }
    }

    /**
     * Gets the value of the search input field.
     * @returns {string}
     */
    getQuery() {
      return this.input ? this.input.value.trim() : "";
    }

    /**
     * Handles search form submit.
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      if (
        !this.getQuery().length ||
        this.querySelector(
          '.predictive-search__item[aria-selected="true"]:not(.js-submit)'
        )
      ) {
        evt.preventDefault();
      }
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      // autocomplete=off prevents browser from restoring value after using back button in Safari
      this.input.setAttribute("value", this.input.value);

      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.close();
        return;
      }

      this.getResults(searchTerm);
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      if (this.productTypeInput) {
        this.productTypeInput.value = evt.detail ? evt.detail.selectedValue : evt.target.value;
      }
      const query = this.getQuery();
      if (query.length > 0) {
        this.getResults(query);
      }
    }

    /**
     * Handles 'focus' events on the search field.
     */
    handleFocus() {
      const searchTerm = this.getQuery();
      if (!searchTerm.length) return;

      if (this.getAttribute("results") === "true") {
        this.open();
      } else {
        this.getResults(searchTerm);
      }
    }

    /**
     * Handles 'keydown' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      // Let tabs script handle keydown events on the tab buttons.
      if (evt.target.matches && evt.target.matches(".tablist__tab")) return;

      switch (evt.key) {
        case "ArrowUp":
        case "ArrowDown":
          evt.preventDefault();

          if (this.hasAttribute("open")) {
            this.handleResultsNav(evt.key);
          }
          break;

        case "Enter":
          this.selectOption();
          break;

        case "Escape":
          this.close();
          // Also close the search modal if it exists
          const modal = document.querySelector('.search-modal');
          if (modal && modal.classList.contains('is-open')) {
            window.closeSearchModal && window.closeSearchModal();
          }
          break;

        // no default
      }
    }

    /**
     * Handles 'keyup' events on the custom element.
     */
    handleKeyup() {
      // If search field is empty after key press, close the results.
      if (!this.getQuery().length) this.close();
    }

    /**
     * Handles keyboard navigation of the results.
     * @param {string} key - Key pressed.
     */
    handleResultsNav(key) {
      const selectedResult = this.querySelector(
        '[role="tabpanel"]:not([hidden]) [aria-selected="true"]'
      );
      const allResults = this.querySelectorAll(
        '[role="tabpanel"]:not([hidden]) .predictive-search__item'
      );
      
      // If no tabpanel results, try to find any results
      const fallbackResults = allResults.length ? allResults : this.querySelectorAll('.predictive-search__item');
      let resultToSelect = fallbackResults[0];

      if (key === "ArrowUp") {
        if (!selectedResult) return;

        // Select the next result up or the last one if we've reached the top of the list.
        resultToSelect =
          selectedResult.previousElementSibling ||
          fallbackResults[fallbackResults.length - 1];
      } else if (key === "ArrowDown") {
        if (selectedResult) {
          // Select the next result down or the first one if we've reached the bottom of the list.
          resultToSelect = selectedResult.nextElementSibling || fallbackResults[0];
        }
      }

      if (this.statusEl) {
        this.statusEl.textContent = "";
      }

      if (resultToSelect) {
        // If the selected item didn't change, do nothing.
        if (resultToSelect === selectedResult) return;

        resultToSelect.setAttribute("aria-selected", "true");
        if (selectedResult)
          selectedResult.setAttribute("aria-selected", "false");

        this.setLiveRegionText(resultToSelect.textContent);
        this.input.setAttribute("aria-activedescendant", resultToSelect.id);
      }
    }

    /**
     * Selects a result
     */
    selectOption() {
      const selectedResult = this.querySelector(
        '[aria-selected="true"] > .js-search-link'
      );
      if (selectedResult) selectedResult.click();
    }

    /**
     * Gets the results for a search term.
     * @param {string} searchTerm - Search query.
     */
    async getResults(searchTerm) {
      this.setLiveRegionLoadingState();

      // Default search fields
      let searchFields = "title,product_type,variants.title,vendor";
      
      // Check theme settings if available
      if (window.theme && window.theme.settings) {
        if (window.theme.settings.pSearchIncludeSkus) searchFields += ",variants.sku";
        if (window.theme.settings.pSearchIncludeTags) searchFields += ",tag";
      }

      // Build search params
      let searchParams = "";
      if (this.productTypeInput && this.productTypeInput.value !== "") {
        searchParams = `q=product_type:${encodeURIComponent(this.productTypeInput.value)} AND ${encodeURIComponent(searchTerm)}`;
      } else {
        searchParams = `q=${encodeURIComponent(searchTerm)}`;
      }

      // Get limits from theme settings or use defaults
      const limit = (window.theme && window.theme.settings && window.theme.settings.pSearchLimit) || 4;
      const limitScope = (window.theme && window.theme.settings && window.theme.settings.pSearchLimitScope) || "each";

      // Only search products
      searchParams += `&${encodeURIComponent("resources[type]")}=product`;
      searchParams += `&${encodeURIComponent("resources[limit]")}=${limit}`;
      searchParams += `&${encodeURIComponent("resources[limit_scope]")}=${limitScope}`;
      searchParams += `&${encodeURIComponent("resources[options][fields]")}=${searchFields}`;
      searchParams += "&section_id=predictive-search";

      const queryKey = searchParams.replace(" ", "-").toLowerCase();
      if (this.cachedResults[queryKey]) {
        this.renderResults(this.cachedResults[queryKey]);
        return;
      }

      try {
        // Get predictive search route from theme or use default
        const predictiveSearchRoute = (window.theme && window.theme.routes && window.theme.routes.predictiveSearch) || "/search/suggest";
        
        const fetchUrl = `${predictiveSearchRoute}?${searchParams}`;
        console.log("Predictive search URL:", fetchUrl);
        
        const response = await fetch(fetchUrl);
        if (!response.ok) throw new Error(response.status);

        const responseText = await response.text();
        console.log("Predictive search response length:", responseText.length);
        
        const tmpl = document.createElement("template");
        tmpl.innerHTML = responseText;

        const resultsEl = tmpl.content.querySelector(
          "#shopify-section-predictive-search"
        );
        
        console.log("Results element found:", !!resultsEl);
        
        if (resultsEl) {
          const resultsMarkup = resultsEl.innerHTML.replace(
            /psearch/g,
            this.input.id
          );
          
          console.log("Results markup length:", resultsMarkup.length);

          this.cachedResults[queryKey] = resultsMarkup;
          this.renderResults(resultsMarkup);
        } else {
          console.log("No results element found in response");
          this.close();
        }
      } catch (error) {
        this.close();
        console.error("Predictive search error:", error);
      }
    }

    /**
     * Sets the live region loading state.
     */
    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
    }

    /**
     * Sets the live region text.
     * @param {string} statusText - Status text.
     */
    setLiveRegionText(statusText) {
      if (!this.statusEl) return;
      
      this.statusEl.setAttribute("aria-hidden", "false");
      this.statusEl.textContent = statusText;

      setTimeout(() => {
        this.statusEl.setAttribute("aria-hidden", "true");
      }, 1000);
    }

    /**
     * Renders the results markup.
     * @param {string} resultsMarkup - Results markup.
     */
    renderResults(resultsMarkup) {
      if (this.results) {
        this.results.innerHTML = resultsMarkup;
      }
      this.setAttribute("results", true);
      this.open();
    }

    /**
     * Opens the results listbox.
     */
    open() {
      if (this.overlay) {
        this.overlay.classList.add("is-visible");
      }
      if (this.resetBtn && this.getQuery().length) {
        this.resetBtn.hidden = false;
      }
      if (this.input) {
        this.input.setAttribute("aria-expanded", "true");
      }
      this.setAttribute("open", "");
      document.body.classList.add("overlay-predictive-search");

      // Add event handlers (so the bound event listeners can be removed).
      this.keydownHandler =
        this.keydownHandler || this.handleKeydown.bind(this);
      this.keyupHandler = this.keyupHandler || this.handleKeyup.bind(this);
      this.resetBtnClickHandler =
        this.resetBtnClickHandler || this.close.bind(this);
      this.overlayClickHandler =
        this.overlayClickHandler || this.close.bind(this);

      // Add event listeners (for while results are open).
      this.addEventListener("keydown", this.keydownHandler);
      this.addEventListener("keyup", this.keyupHandler);
      if (this.resetBtn) {
        this.resetBtn.addEventListener("click", this.resetBtnClickHandler);
      }
      if (this.overlay) {
        this.overlay.addEventListener("click", this.overlayClickHandler);
      }
    }

    /**
     * Closes the results listbox.
     * @param {object} evt - Event object.
     */
    close(evt) {
      // If reset button was clicked, empty the input field.
      if (evt && this.resetBtn && evt.target === this.resetBtn) {
        if (this.input) {
          this.input.value = "";
        }
        this.removeAttribute("results");
        if (this.input) {
          this.input.focus();
        }
      }

      // Clear the results when search is empty
      if (this.results && !this.getQuery().length) {
        this.results.innerHTML = "";
        this.removeAttribute("results");
      }

      // Deselect the selected result (if there is one).
      const selected = this.querySelector(
        '.predictive-search__item[aria-selected="true"]'
      );
      if (selected) selected.setAttribute("aria-selected", "false");

      this.removeAttribute("open");
      if (this.resetBtn) {
        this.resetBtn.hidden = true;
      }
      if (this.overlay) {
        this.overlay.classList.remove("is-visible");
      }
      if (this.input) {
        this.input.setAttribute("aria-activedescendant", "");
        this.input.setAttribute("aria-expanded", "false");
      }
      document.body.classList.remove("overlay-predictive-search");

      // Remove event listeners added on search results opening.
      this.removeEventListener("keydown", this.keydownHandler);
      if (this.resetBtn) {
        this.resetBtn.removeEventListener("click", this.resetBtnClickHandler);
      }
      if (this.overlay) {
        this.overlay.removeEventListener("click", this.overlayClickHandler);
      }
    }
  }

  customElements.define("predictive-search", PredictiveSearch);
}

/**
 * Tabbed Content Component for search results tabs
 */
if (!customElements.get("tabbed-content")) {
  class TabbedContent extends HTMLElement {
    constructor() {
      super();
      this.tabs = this.querySelectorAll('[role="tab"]');
      this.panels = this.querySelectorAll('[role="tabpanel"]');
      
      if (this.tabs.length) {
        this.addListeners();
      }
    }

    addListeners() {
      this.tabs.forEach((tab) => {
        tab.addEventListener("click", this.handleTabClick.bind(this));
        tab.addEventListener("keydown", this.handleTabKeydown.bind(this));
      });
    }

    handleTabClick(evt) {
      const clickedTab = evt.currentTarget;
      this.selectTab(clickedTab);
    }

    handleTabKeydown(evt) {
      const tabsArray = Array.from(this.tabs);
      const currentIndex = tabsArray.indexOf(evt.currentTarget);
      let newIndex;

      switch (evt.key) {
        case "ArrowLeft":
          newIndex = currentIndex - 1;
          if (newIndex < 0) newIndex = tabsArray.length - 1;
          tabsArray[newIndex].focus();
          this.selectTab(tabsArray[newIndex]);
          evt.preventDefault();
          break;
        case "ArrowRight":
          newIndex = currentIndex + 1;
          if (newIndex >= tabsArray.length) newIndex = 0;
          tabsArray[newIndex].focus();
          this.selectTab(tabsArray[newIndex]);
          evt.preventDefault();
          break;
        case "Home":
          tabsArray[0].focus();
          this.selectTab(tabsArray[0]);
          evt.preventDefault();
          break;
        case "End":
          tabsArray[tabsArray.length - 1].focus();
          this.selectTab(tabsArray[tabsArray.length - 1]);
          evt.preventDefault();
          break;
      }
    }

    selectTab(selectedTab) {
      // Deselect all tabs
      this.tabs.forEach((tab) => {
        tab.setAttribute("aria-selected", "false");
        tab.setAttribute("tabindex", "-1");
      });

      // Select the clicked tab
      selectedTab.setAttribute("aria-selected", "true");
      selectedTab.setAttribute("tabindex", "0");

      // Hide all panels
      this.panels.forEach((panel) => {
        panel.setAttribute("hidden", "");
      });

      // Show the selected panel
      const panelId = selectedTab.getAttribute("aria-controls");
      const selectedPanel = this.querySelector(`#${panelId}`);
      if (selectedPanel) {
        selectedPanel.removeAttribute("hidden");
      }
    }
  }

  customElements.define("tabbed-content", TabbedContent);
}

/**
 * Search Modal Controller
 * Handles opening/closing the search modal
 */
(function() {
  let searchModal = null;
  let searchOverlay = null;
  let searchInput = null;
  let searchTrigger = null; // element to return focus to on close

  // The modal lives inside .main_header, next to <header>; everything else
  // on the page is made inert while it is open (WCAG 2.4.3).
  function setPageInert(on) {
    ['#MainContent', '#shopify-section-footer', '#shopify-section-announcement-bar', '.main_header > header', '#menu']
      .forEach(sel => { const el = document.querySelector(sel); if (el) el.inert = on; });
  }

  function openSearchModal() {
    if (!searchModal) return;
    searchTrigger = document.activeElement;
    setPageInert(true);
    
    searchModal.classList.add('is-open');
    searchOverlay.classList.add('is-visible');
    document.body.classList.add('search-modal-open');
    
    // Dispatch custom event for other scripts to listen to
    document.dispatchEvent(new CustomEvent('search-modal:open'));
    
    setTimeout(() => {
      if (searchInput) {
        searchInput.focus();
      }
    }, 100);
  }

  function closeSearchModal() {
    if (!searchModal) return;
    
    searchModal.classList.remove('is-open');
    searchOverlay.classList.remove('is-visible');
    document.body.classList.remove('search-modal-open');
    document.body.classList.remove('overlay-predictive-search');
    setPageInert(false);
    if (searchTrigger && typeof searchTrigger.focus === 'function' && document.body.contains(searchTrigger)) {
      searchTrigger.focus();
    }
    searchTrigger = null;
    
    // Dispatch custom event for other scripts to listen to
    document.dispatchEvent(new CustomEvent('search-modal:close'));
  }

  // Expose functions globally
  window.openSearchModal = openSearchModal;
  window.closeSearchModal = closeSearchModal;

  // Initialize on DOM ready
  document.addEventListener('DOMContentLoaded', function() {
    searchModal = document.querySelector('.search-modal');
    searchOverlay = document.querySelector('.search-modal-overlay');
    searchInput = searchModal ? searchModal.querySelector('.js-search-input') : null;

    // Add click listeners to search trigger buttons
    const searchTriggers = document.querySelectorAll('[data-search-trigger], .btn.search__mobile, .header_right .btn:has(.icon-search)');
    searchTriggers.forEach(trigger => {
      trigger.addEventListener('click', function(e) {
        e.preventDefault();
        openSearchModal();
      });
    });

    // Close button
    const closeBtn = searchModal ? searchModal.querySelector('.search-modal__close') : null;
    if (closeBtn) {
      closeBtn.addEventListener('click', closeSearchModal);
    }

    // Close on overlay click
    if (searchOverlay) {
      searchOverlay.addEventListener('click', closeSearchModal);
    }

    // Close on Escape key
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && searchModal && searchModal.classList.contains('is-open')) {
        closeSearchModal();
      }
    });

    // Close when clicking outside the search modal
    document.addEventListener('click', function(e) {
      if (!searchModal || !searchModal.classList.contains('is-open')) return;
      
      // Check if click is outside the search modal
      const isClickInsideModal = searchModal.contains(e.target);
      const isClickOnTrigger = e.target.closest('[data-search-trigger]');
      
      if (!isClickInsideModal && !isClickOnTrigger) {
        closeSearchModal();
      }
    });
  });
})();
